import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FullscreenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  noPadding?: boolean;
  bodyContainerClassName?: string;
  headerLeft?: ReactNode;
  showCloseButton?: boolean;
  headerClassName?: string;
  titleClassName?: string;
  closeButtonClassName?: string;
  footerClassName?: string;
}

export function FullscreenDialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  bodyContainerClassName,
  headerLeft,
  showCloseButton = true,
  headerClassName,
  titleClassName,
  closeButtonClassName,
  footerClassName,
  noPadding = false,
}: FullscreenDialogProps) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          />

          {/* Dialog Content */}
          <motion.div
            className="fixed inset-0 z-50 flex flex-col w-full h-full bg-background"
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {/* Header */}
            <div
              className={cn(
                'border-b shrink-0 px-6 py-4 h-16 sticky top-0 bg-background z-10',
                headerClassName
              )}
            >
              {headerLeft && (
                <div className="absolute left-6 top-1/2 z-10 -translate-y-1/2 flex items-center justify-center">
                  {headerLeft}
                </div>
              )}
              <div className="pointer-events-none flex items-center justify-center absolute top-0 bottom-0 left-0 right-0 w-full">
                <h3 className={cn('font-medium', titleClassName)}>{title}</h3>
              </div>
              {showCloseButton && (
                <div className="absolute right-6 top-1/2 z-10 -translate-y-1/2 flex items-center justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={closeButtonClassName}
                    onClick={() => onOpenChange(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Content - This is the scrollable area */}
            <div
              className={cn(
                'flex-1 overflow-y-auto overflow-x-hidden',
                bodyContainerClassName
              )}
            >
              {noPadding ? children : <div className="p-6">{children}</div>}
            </div>

            {/* Footer */}
            {footer && (
              <div
                className={cn(
                  'border-t p-4 flex justify-center gap-2 shrink-0 bg-background z-10',
                  footerClassName
                )}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
